import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  Button,
  Select,
  Option,
  CircularProgress,
  Alert,
  Divider,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/joy';
import {
  Notifications as NotificationsIcon,
  FilterList,
  MarkEmailRead,
  MoreVert,
  Schedule,
  Edit,
  SwapHoriz,
  Info,
  Alarm,
  Circle,
  CheckCircle,
  Delete,
  SelectAll,
  Clear
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { NotificationService } from '../services/notificationService';
import { 
  Notification, 
  NotificationQuery, 
  NotificationFilter, 
  NotificationCategory,
  NotificationPriority,
  CATEGORY_DISPLAY_NAMES, 
  PRIORITY_COLORS, 
  CATEGORY_ICONS 
} from '../types/notification';

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    pages: 0,
    current: 1,
    hasNext: false,
    hasPrev: false
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotifications, setSelectedNotifications] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState<NotificationQuery>({
    page: 1,
    limit: 20,
    filter: 'all'
  });
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedNotificationId, setSelectedNotificationId] = useState<number | null>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await NotificationService.getNotifications(filters);
      setNotifications(response.notifications);
      setPagination(response.pagination);
      setUnreadCount(response.unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const markAsRead = useCallback(async (id: number, isRead: boolean = true) => {
    try {
      await NotificationService.markAsRead(id, isRead);
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      await NotificationService.markAllAsRead();
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [fetchNotifications]);

  const deleteNotification = useCallback(async (id: number) => {
    try {
      await NotificationService.deleteNotification(id);
      await fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [fetchNotifications]);

  const bulkActions = useCallback(async (action: 'mark_read' | 'mark_unread' | 'delete') => {
    if (selectedNotifications.size === 0) return;

    try {
      await NotificationService.bulkActions({
        notificationIds: Array.from(selectedNotifications),
        action
      });
      setSelectedNotifications(new Set());
      await fetchNotifications();
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  }, [selectedNotifications, fetchNotifications]);

  const handleFilterChange = useCallback((key: keyof NotificationQuery, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filters change
    }));
  }, []);



  const handleNotificationClick = useCallback((notification: Notification) => {
    setSelectedNotification(notification);
    setModalOpen(true);
    
    // Mark as read when opened
    if (!notification.is_read) {
      markAsRead(notification.id, true);
    }
  }, [markAsRead]);

  const handleSelectAll = useCallback(() => {
    if (selectedNotifications.size === notifications.length) {
      setSelectedNotifications(new Set());
    } else {
      setSelectedNotifications(new Set(notifications.map(n => n.id)));
    }
  }, [notifications, selectedNotifications.size]);

  const handleSelectNotification = useCallback((id: number) => {
    const newSelected = new Set(selectedNotifications);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedNotifications(newSelected);
  }, [selectedNotifications]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, notificationId: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedNotificationId(notificationId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedNotificationId(null);
  }, []);

  const handleMenuAction = useCallback(async (action: 'mark_read' | 'mark_unread' | 'delete') => {
    if (!selectedNotificationId) return;

    try {
      switch (action) {
        case 'mark_read':
          await markAsRead(selectedNotificationId, true);
          break;
        case 'mark_unread':
          await markAsRead(selectedNotificationId, false);
          break;
        case 'delete':
          await deleteNotification(selectedNotificationId);
          break;
      }
      handleMenuClose();
    } catch (error) {
      console.error('Error performing menu action:', error);
    }
  }, [selectedNotificationId, markAsRead, deleteNotification, handleMenuClose]);

  const getCategoryIcon = useCallback((category: string) => {
    const iconName = CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS];
    switch (iconName) {
      case 'Schedule':
        return <Schedule />;
      case 'Edit':
        return <Edit />;
      case 'SwapHoriz':
        return <SwapHoriz />;
      case 'Info':
        return <Info />;
      case 'Alarm':
        return <Alarm />;
      default:
        return <NotificationsIcon />;
    }
  }, []);

  const formatTimeAgo = useCallback((dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Handle clicking outside the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (anchorEl && !anchorEl.contains(event.target as Node)) {
        handleMenuClose();
      }
    };

    if (anchorEl) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorEl, handleMenuClose]);

  return (
    <Box>
      <Typography level="h2" component="h1" sx={{ mb: 3 }}>
        Notifications
      </Typography>

      {/* Filters and Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Select
                size="sm"
                value={filters.filter}
                onChange={(_, value) => handleFilterChange('filter', value)}
                startDecorator={<FilterList />}
              >
                <Option value="all">All</Option>
                <Option value="unread">Unread</Option>
                <Option value="read">Read</Option>
              </Select>

              <Select
                size="sm"
                value={filters.category || ''}
                onChange={(_, value) => handleFilterChange('category', value || undefined)}
                placeholder="All Categories"
              >
                <Option value="">All Categories</Option>
                {Object.entries(CATEGORY_DISPLAY_NAMES).map(([key, label]) => (
                  <Option key={key} value={key}>{label}</Option>
                ))}
              </Select>

              <Select
                size="sm"
                value={filters.priority || ''}
                onChange={(_, value) => handleFilterChange('priority', value || undefined)}
                placeholder="All Priorities"
              >
                <Option value="">All Priorities</Option>
                <Option value="low">Low</Option>
                <Option value="normal">Normal</Option>
                <Option value="high">High</Option>
                <Option value="urgent">Urgent</Option>
              </Select>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedNotifications.size > 0 && (
                <>
                  <Button
                    size="sm"
                    variant="outlined"
                    startDecorator={<CheckCircle />}
                    onClick={() => bulkActions('mark_read')}
                  >
                    Mark Selected Read
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="danger"
                    startDecorator={<Delete />}
                    onClick={() => bulkActions('delete')}
                  >
                    Delete Selected
                  </Button>
                </>
              )}
              
              <Button
                size="sm"
                variant="outlined"
                startDecorator={<MarkEmailRead />}
                onClick={markAllAsRead}
                disabled={unreadCount === 0}
              >
                Mark All Read
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Notifications List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : notifications.length === 0 ? (
        <Alert variant="soft" color="neutral">
          No notifications found
        </Alert>
      ) : (
        <Card>
          <CardContent>
            <List>
              {notifications.map((notification) => (
                <ListItem
                  key={notification.id}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'background.level1'
                    }
                  }}
                >
                  <ListItemDecorator>
                    <Checkbox
                      checked={selectedNotifications.has(notification.id)}
                      onChange={() => handleSelectNotification(notification.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </ListItemDecorator>
                  
                  <ListItemDecorator>
                    <Box sx={{ position: 'relative' }}>
                      {getCategoryIcon(notification.category)}
                      {!notification.is_read && (
                        <Circle
                          sx={{
                            position: 'absolute',
                            top: -4,
                            right: -4,
                            fontSize: 12,
                            color: 'primary.500'
                          }}
                        />
                      )}
                    </Box>
                  </ListItemDecorator>
                  
                  <ListItemContent onClick={() => handleNotificationClick(notification)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography level="body-sm" fontWeight="md">
                        {notification.title}
                      </Typography>
                      <Chip
                        size="sm"
                        variant="soft"
                        sx={{
                          backgroundColor: PRIORITY_COLORS[notification.priority],
                          color: 'white',
                          fontSize: '0.75rem'
                        }}
                      >
                        {notification.priority}
                      </Chip>
                      <Chip
                        size="sm"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {CATEGORY_DISPLAY_NAMES[notification.category as keyof typeof CATEGORY_DISPLAY_NAMES]}
                      </Chip>
                    </Box>
                    
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      {notification.message}
                    </Typography>
                    
                    <Typography level="body-xs" color="neutral">
                      {formatTimeAgo(notification.created_at)}
                    </Typography>
                  </ListItemContent>
                  
                  <IconButton
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMenuOpen(e, notification.id);
                    }}
                  >
                    <MoreVert />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Typography level="body-sm" color="neutral">
            Page {pagination.current} of {pagination.pages}
          </Typography>
        </Box>
      )}

      {/* Notification Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <ModalDialog size="md">
          <ModalClose />
          <DialogTitle>
            {selectedNotification?.title}
          </DialogTitle>
          <DialogContent>
            {selectedNotification && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    size="sm"
                    variant="soft"
                    sx={{
                      backgroundColor: PRIORITY_COLORS[selectedNotification.priority],
                      color: 'white'
                    }}
                  >
                    {selectedNotification.priority}
                  </Chip>
                  <Typography level="body-sm" color="neutral">
                    {CATEGORY_DISPLAY_NAMES[selectedNotification.category as keyof typeof CATEGORY_DISPLAY_NAMES]}
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    {formatTimeAgo(selectedNotification.created_at)}
                  </Typography>
                </Box>
                
                <Typography level="body-md" sx={{ mb: 2 }}>
                  {selectedNotification.message}
                </Typography>
                
                {selectedNotification.action_url && selectedNotification.action_label && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const url = selectedNotification.action_url!.replace(
                        /^\/schedules(\?|$)/,
                        '/schedule$1'
                      );
                      window.location.href = url;
                    }}
                  >
                    {selectedNotification.action_label}
                  </Button>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                if (selectedNotification) {
                  markAsRead(selectedNotification.id, !selectedNotification.is_read);
                }
              }}
            >
              {selectedNotification?.is_read ? 'Mark as Unread' : 'Mark as Read'}
            </Button>
            <Button
              variant="outlined"
              color="danger"
              onClick={() => {
                if (selectedNotification) {
                  deleteNotification(selectedNotification.id);
                  setModalOpen(false);
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {selectedNotificationId && (() => {
          const selectedNotification = notifications.find(n => n.id === selectedNotificationId);
          const isRead = selectedNotification?.is_read || false;
          
          return (
            <>
              <MenuItem onClick={() => handleMenuAction(isRead ? 'mark_unread' : 'mark_read')}>
                {isRead ? <Circle sx={{ mr: 1 }} /> : <CheckCircle sx={{ mr: 1 }} />}
                {isRead ? 'Mark as Unread' : 'Mark as Read'}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleMenuAction('delete')} color="danger">
                <Delete sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </>
          );
        })()}
      </Menu>
    </Box>
  );
};

export default NotificationsPage; 